import { cn } from "@util/tailwind";

type FeedbackCategory = "Brilliant" | "Excellent" | "Good" | "Inaccuracy" | "Mistake";

interface FeedbackCategoryBadgeProps {
    category: FeedbackCategory;
    count: number;
    language?: string;
    /** When true, render the filled "active filter" treatment. */
    selected?: boolean;
}

const translatedLabels: Record<string, Record<string, string>> = {
    Malay: {
        Brilliant: "Cemerlang",
        Excellent: "Sangat Baik",
        Good: "Baik",
        Inaccuracy: "Kurang Tepat",
        Mistake: "Kesilapan",
    },
    "id-ID": {
        Brilliant: "Cemerlang",
        Excellent: "Sangat Baik",
        Good: "Baik",
        Inaccuracy: "Kurang Tepat",
        Mistake: "Kesalahan",
    },
};

const defaultLabels: Record<FeedbackCategory, string> = {
    Brilliant: "Brilliant",
    Excellent: "Excellent",
    Good: "Good",
    Inaccuracy: "Inaccuracy",
    Mistake: "Mistake",
};

const idleStyles: Record<FeedbackCategory, string> = {
    Brilliant: "bg-purple-50 text-purple-700 border-purple-100 hover:bg-purple-100",
    Excellent: "bg-green-50 text-green-700 border-green-100 hover:bg-green-100",
    Good: "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100",
    Inaccuracy: "bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100",
    Mistake: "bg-red-50 text-red-700 border-red-100 hover:bg-red-100",
};

const activeStyles: Record<FeedbackCategory, string> = {
    Brilliant: "bg-purple-600 text-white border-purple-600 shadow-sm",
    Excellent: "bg-green-600 text-white border-green-600 shadow-sm",
    Good: "bg-emerald-600 text-white border-emerald-600 shadow-sm",
    Inaccuracy: "bg-amber-500 text-white border-amber-500 shadow-sm",
    Mistake: "bg-red-600 text-white border-red-600 shadow-sm",
};

const activeCountStyles: Record<FeedbackCategory, string> = {
    Brilliant: "bg-purple-800/40 text-white",
    Excellent: "bg-green-800/40 text-white",
    Good: "bg-emerald-800/40 text-white",
    Inaccuracy: "bg-amber-700/40 text-white",
    Mistake: "bg-red-800/40 text-white",
};

const idleCountStyles: Record<FeedbackCategory, string> = {
    Brilliant: "bg-purple-100 text-purple-800",
    Excellent: "bg-green-100 text-green-800",
    Good: "bg-emerald-100 text-emerald-800",
    Inaccuracy: "bg-amber-100 text-amber-800",
    Mistake: "bg-red-100 text-red-800",
};

export default function FeedbackCategoryBadge({
    category,
    count,
    language,
    selected = false,
}: FeedbackCategoryBadgeProps) {
    const labels = (language && translatedLabels[language]) || defaultLabels;

    return (
        <span
            className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                selected ? activeStyles[category] : idleStyles[category]
            )}
        >
            {labels[category]}
            {count > 0 && (
                <span
                    className={cn(
                        "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold tabular-nums",
                        selected ? activeCountStyles[category] : idleCountStyles[category]
                    )}
                >
                    {count}
                </span>
            )}
        </span>
    );
}
